<!-- タイムラインオーバーレイコンポーネント（Break/強勢/Monotone可視化） -->
<template>
  <div class="timeline-overlay-container">
    <h4 class="timeline-title">⏱️ 時間軸分析</h4>
    
    <!-- メインタイムライントラック -->
    <div class="timeline-track" ref="trackRef">
      <div
        v-for="(segment, index) in timelineData"
        :key="index"
        class="timeline-segment"
        :style="getSegmentStyle(segment)"
        :class="getSegmentClass(segment)"
        :title="getSegmentTooltip(segment)"
        @click="onSegmentClick(segment)"
      >
        <span class="segment-word">{{ segment.word }}</span>
        
        <!-- Break表示（縦線） -->
        <span
          v-for="(breakPoint, bIndex) in segment.breaks"
          :key="`break-${bIndex}`"
          class="timeline-break"
          :class="breakPoint.type === 'unexpected' ? 'break-unexpected' : 'break-missing'"
          :style="getBreakStyle(segment, breakPoint)"
          :title="getBreakTooltip(breakPoint)"
        ></span>
        
        <!-- Syllable強勢表示 -->
        <div
          v-for="(syllable, sIndex) in segment.syllables"
          :key="`syllable-${sIndex}`"
          class="timeline-syllable"
          :class="getSyllableClass(syllable)"
          :style="getSyllableStyle(segment, syllable)"
          :title="getSyllableTooltip(syllable)"
        ></div>
      </div>
    </div>
    
    <!-- Monotone区間の表示 -->
    <div class="timeline-monotone" v-if="hasMonotoneSegments">
      <span class="monotone-label">⚠️ 単調な発音区間</span>
      <div
        v-for="(segment, index) in monotoneSegments"
        :key="`mono-${index}`"
        class="monotone-segment"
        :style="getMonotoneStyle(segment)"
        :title="`単調な発音: ${segment.word}`"
      ></div>
    </div>
    
    <!-- 凡例 -->
    <div class="timeline-legend">
      <span class="legend-item">
        <span class="legend-color break-unexpected"></span>
        予期しない休符
      </span>
      <span class="legend-item">
        <span class="legend-color break-missing"></span>
        不足休符
      </span>
      <span class="legend-item">
        <span class="legend-color stress-primary"></span>
        強勢
      </span>
      <span class="legend-item">
        <span class="legend-color monotone-segment"></span>
        単調な発音
      </span>
    </div>
    
    <!-- スクラブコントロール（将来実装: 音声再生連動） -->
    <div class="timeline-scrub" v-if="showScrub">
      <input
        type="range"
        :min="0"
        :max="totalDuration"
        :value="scrubPosition"
        @input="onScrubChange"
        class="scrub-slider"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'

const props = defineProps({
  timelineData: {
    type: Array,
    required: true
  },
  targetWord: {
    type: Object,
    default: null
  },
  totalDuration: {
    type: Number,
    default: 1
  },
  showScrub: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['segment-click', 'scrub-change'])

const trackRef = ref(null)
const scrubPosition = ref(0)

// Monotone区間があるか
const hasMonotoneSegments = computed(() => {
  return props.timelineData.some(segment => segment.isMonotone)
})

// Monotone区間のリスト
const monotoneSegments = computed(() => {
  return props.timelineData.filter(segment => segment.isMonotone)
})

// セグメントスタイル
const getSegmentStyle = (segment) => {
  const leftPercent = (segment.start / props.totalDuration) * 100
  const widthPercent = (segment.duration / props.totalDuration) * 100
  
  return {
    left: `${leftPercent}%`,
    width: `${widthPercent}%`
  }
}

// セグメントクラス
const getSegmentClass = (segment) => {
  const classes = []
  
  if (segment.breaks && segment.breaks.length > 0) {
    classes.push('has-breaks')
  }
  
  if (segment.isMonotone) {
    classes.push('is-monotone')
  }
  
  // ターゲット語かどうか
  if (props.targetWord && (
    segment.word === props.targetWord.word ||
    segment.word === props.targetWord.expectedWord
  )) {
    classes.push('is-target')
  }
  
  return classes.join(' ')
}

// Breakスタイル
const getBreakStyle = (segment, breakPoint) => {
  // Break位置をセグメント内の相対位置で計算
  const breakTime = breakPoint.position / 10000000 // 秒
  const segmentStart = segment.start
  const segmentDuration = segment.duration
    
  const relativePosition = ((breakTime - segmentStart) / segmentDuration) * 100
  
  return {
    left: `${relativePosition}%`
  }
}

// Syllableスタイル
const getSyllableStyle = (segment, syllable) => {
  const syllableStart = syllable.offset / 10000000
  const syllableDuration = syllable.duration / 10000000
  const segmentStart = segment.start
  const segmentDuration = segment.duration
  
  const leftPercent = ((syllableStart - segmentStart) / segmentDuration) * 100
  const widthPercent = (syllableDuration / segmentDuration) * 100
  
  return {
    left: `${leftPercent}%`,
    width: `${widthPercent}%`
  }
}

// Monotoneスタイル
const getMonotoneStyle = (segment) => {
  const leftPercent = (segment.start / props.totalDuration) * 100
  const widthPercent = (segment.duration / props.totalDuration) * 100
    
  return {
    left: `${leftPercent}%`,
    width: `${widthPercent}%`
  }
}

// Syllableクラス
const getSyllableClass = (syllable) => {
  if (syllable.stress === 'primary') return 'stress-primary'
  if (syllable.stress === 'secondary') return 'stress-secondary'
  return ''
}

// ツールチップ
const getSegmentTooltip = (segment) => {
  const parts = [`単語: ${segment.word}`]
  
  if (segment.breaks && segment.breaks.length > 0) {
    parts.push(`休符問題: ${segment.breaks.length}箇所`)
  }
  
  if (segment.isMonotone) {
    parts.push('単調な発音')
  }
  
  return parts.join(', ')
}

const getBreakTooltip = (breakPoint) => {
  return breakPoint.type === 'unexpected'
    ? '予期しない休符'
    : '必要な休符が不足'
}

const getSyllableTooltip = (syllable) => {
  return `${syllable.grapheme || ''} (${syllable.stress || 'none'})`
}

// セグメントクリック
const onSegmentClick = (segment) => {
  emit('segment-click', segment)
}

// スクラブ変更
const onScrubChange = (event) => {
  scrubPosition.value = parseFloat(event.target.value)
  emit('scrub-change', scrubPosition.value)
}
</script>

<style scoped>
.timeline-overlay-container {
  padding: 1rem;
  background: #fafafa;
  border-radius: 8px;
  border-left: 4px solid #6366f1;
}

.timeline-title {
  margin: 0 0 1rem 0;
  color: #1f2937;
  font-size: 1.1rem;
}

.timeline-track {
  position: relative;
  height: 60px;
  background: #e5e7eb;
  border-radius: 4px;
  margin-bottom: 1rem;
  overflow: hidden;
}

.timeline-segment {
  position: absolute;
  height: 100%;
  background: rgba(59, 130, 246, 0.3);
  border: 1px solid rgba(59, 130, 246, 0.5);
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.85rem;
  font-weight: bold;
  color: #1f2937;
  overflow: visible;
  cursor: pointer;
  transition: all 0.2s;
}

.timeline-segment:hover {
  background: rgba(59, 130, 246, 0.4);
  transform: translateY(-2px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.timeline-segment.has-breaks {
  border-color: #ef4444;
  background: rgba(239, 68, 68, 0.2);
}

.timeline-segment.is-monotone {
  border-color: #f59e0b;
  background: rgba(245, 158, 11, 0.2);
}

.timeline-segment.is-target {
  border: 2px solid #ea580c;
  box-shadow: 0 0 8px rgba(234, 88, 12, 0.4);
}

.segment-word {
  z-index: 2;
  text-align: center;
  padding: 0.2rem;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 4px;
}

.timeline-break {
  position: absolute;
  width: 3px;
  height: 100%;
  z-index: 3;
  top: 0;
  pointer-events: none;
}

.timeline-break.break-unexpected {
  background: #ef4444;
}

.timeline-break.break-missing {
  background: #f59e0b;
}

.timeline-syllable {
  position: absolute;
  height: 8px;
  bottom: 0;
  background: rgba(16, 185, 129, 0.5);
  border-radius: 2px;
  z-index: 1;
  pointer-events: none;
}

.timeline-syllable.stress-primary {
  background: rgba(16, 185, 129, 0.8);
  height: 12px;
  border: 2px solid #10b981;
}

.timeline-syllable.stress-secondary {
  background: rgba(59, 130, 246, 0.6);
  height: 10px;
}

.timeline-monotone {
  position: relative;
  height: 20px;
  background: rgba(245, 158, 11, 0.1);
  border-radius: 4px;
  margin-bottom: 1rem;
  overflow: visible;
}

.monotone-label {
  position: absolute;
  left: 0.5rem;
  top: 50%;
  transform: translateY(-50%);
  font-size: 0.85rem;
  color: #92400e;
  font-weight: bold;
  z-index: 2;
}

.monotone-segment {
  position: absolute;
  height: 100%;
  background: rgba(245, 158, 11, 0.4);
  border-left: 2px solid #f59e0b;
  border-right: 2px solid #f59e0b;
}

.timeline-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  font-size: 0.85rem;
  color: #6b7280;
  margin-bottom: 0.5rem;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.legend-color {
  width: 16px;
  height: 16px;
  border-radius: 2px;
}

.legend-color.break-unexpected {
  background: #ef4444;
}

.legend-color.break-missing {
  background: #f59e0b;
}

.legend-color.stress-primary {
  background: #10b981;
}

.legend-color.monotone-segment {
  background: rgba(245, 158, 11, 0.4);
}

.timeline-scrub {
  margin-top: 1rem;
}

.scrub-slider {
  width: 100%;
  height: 6px;
  border-radius: 3px;
  background: #e5e7eb;
  outline: none;
  -webkit-appearance: none;
}

.scrub-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #3b82f6;
  cursor: pointer;
}

.scrub-slider::-moz-range-thumb {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #3b82f6;
  cursor: pointer;
  border: none;
}

@media (max-width: 768px) {
  .timeline-track {
    height: 50px;
  }
  
  .segment-word {
    font-size: 0.75rem;
  }
  
  .timeline-legend {
    flex-direction: column;
    gap: 0.5rem;
  }
}
</style>

